'use client';

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { HelpTopicId } from '@/lib/help/content';

/**
 * Contextual help state. Any component in the dashboard can open the help
 * side-drawer — either on the index (no topic) or deep-linked to a topic.
 */
interface HelpContextValue {
  /** Whether the help drawer is open. */
  isOpen: boolean;
  /** The topic currently shown, or null for the index. */
  activeTopicId: string | null;
  /** Open the drawer. Pass a topic id to deep-link, or omit for the index. */
  openHelp: (topicId?: HelpTopicId) => void;
  /** Close the drawer. */
  closeHelp: () => void;
  /** Navigate to a topic without closing (used by index/related links). */
  showTopic: (topicId: string | null) => void;
}

const HelpContext = createContext<HelpContextValue | null>(null);

/**
 * Provides contextual help state to the dashboard. Mount once near the root of
 * the authenticated layout, alongside {@link HelpDrawer}.
 */
export function HelpProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTopicId, setActiveTopicId] = useState<string | null>(null);

  const openHelp = useCallback((topicId?: HelpTopicId) => {
    setActiveTopicId(topicId ?? null);
    setIsOpen(true);
  }, []);

  const closeHelp = useCallback(() => {
    setIsOpen(false);
  }, []);

  const showTopic = useCallback((topicId: string | null) => {
    setActiveTopicId(topicId);
  }, []);

  const value = useMemo<HelpContextValue>(
    () => ({ isOpen, activeTopicId, openHelp, closeHelp, showTopic }),
    [isOpen, activeTopicId, openHelp, closeHelp, showTopic]
  );

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

/**
 * Access the contextual help controls.
 *
 * @throws If used outside of a {@link HelpProvider}.
 */
export function useHelp(): HelpContextValue {
  const ctx = useContext(HelpContext);
  if (!ctx) {
    throw new Error('useHelp must be used within a HelpProvider');
  }
  return ctx;
}
